// Claude: split from adminAPI.js — sellers, seller subscriptions, seller assets 14-04-2026
import { api } from "./config/axiosConfig.js";
import { store } from "../redux/Store.ts";
import { responseHandler } from "../Utils/handler.ts";

export const AdminSellerAPI = {

  // Sellers
  postSeller: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/sellers/",
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  getSeller: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/sellers/",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params: params,
    });
    return response.data;
  },

  getEventSeller: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/event-sellers/",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params: params,
    });
    return response.data;
  },

  // Claude: unlink seller from one event only 07-Apr-2026
  unlinkSellerFromEvent: async (sellerId, eventId) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/event/${eventId}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: get all eventIds for a seller — admin multi-event UI 06-03-2026
  getSellerEventIds: async (sellerId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/event-sellers/${sellerId}/events`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getSellerById: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${id}/`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  putSeller: async (id, data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${id}`,
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  deleteSeller: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: bulk delete — single request instead of N parallel requests 24-04-2026
  bulkDeleteSellers: async (ids) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/bulk`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
      data: { ids },
    });
    return response.data;
  },

  // Claude: check if another verified seller shares same full email domain 21-03-2026
  checkSellerDomainConflict: async (sellerId) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/domain-verified-check`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin records seller subscription payment → writes to seller_statements 25-Apr-2026
  addSellerPaymentRecord: async (sellerId, data) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/add-payment-record`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response.data;
  },

  // Claude: admin views all statement records for a seller 25-Apr-2026
  getSellerStatements: async (sellerId) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/statements`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin renews seller subscription — assigns plan, resets per-cycle counters + restores isPlanActive 20-04-2026
  renewSellerSubscription: async (sellerId, data = {}) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/renew-subscription`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response.data;
  },

  // Claude: admin override seller verification status 26-02-2026
  overrideSellerVerification: async (sellerId, status, reason) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/sellers/${sellerId}/verification`,
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      data: { status, reason },
    });
    return response.data;
  },

  // Seller Subscriptions
  // Claude: changed from path param to query param for optional event filter 18-02-2026
  getSellerSubscriptions: async (eventId, params, cancel = false) => {
    const { token } = await store.getState().user;
    const queryParams = { ...params };
    if (eventId) queryParams.eventId = eventId;
    const response = await api.request({
      url: "/seller-subscriptions",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params: queryParams,
    });
    return response.data;
  },

  addSellerSubscriptionPlan: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/seller-subscriptions`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  updateSellerSubscriptionPlan: async (id, data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/seller-subscriptions/${id}`,
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  getSellerSubscriptionUsage: async (id) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/seller-subscriptions/${id}/usage`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  deleteSellerSubscription: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/seller-subscriptions/${id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  requestSellerSubscriptionPlan: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/seller-subscriptions/sendEmail`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  // Seller Digital Assets
  getSellerAssets: async (sellerId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `sellers/digital-assets/${sellerId}`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  addSellerAsset: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "sellers/digital-assets",
      method: "POST",
      data: data,
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  deleteSellerAsset: async (assetId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "sellers/digital-assets/" + assetId,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  getSellerProfile: async (sellerId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "sellers/event-sellers/" + sellerId,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  // Claude: distinct productTypeTags for admin edit-seller hints — all sellers 20-05-2026
  getSellerProductTags: async () => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: "sellers/admin-tags",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: CSV export — triggers browser file download 27-02-2026
  exportSellers: async (params) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/sellers/export",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params,
      responseType: "blob",
    });
    const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sellers_${ts}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

};
